import { useEffect, useState, useCallback } from "react";
import { fetchCounterparties as fetchCounterpartiesApi } from "../../http/api";

// Normalize id to a stable string across all payload shapes
const normalizeId = (obj) => {
  const idVal =
    obj?.id ??
    obj?.counterPartyMstId ??
    obj?.counterpartyMstId ??
    obj?.counterpartyId ??
    obj?.counterPartyId;
  return idVal !== undefined && idVal !== null && idVal !== "" ? String(idVal) : "";
};

export const useCounterparties = (
  token,
  { page = 0, size = 10000, columnToSort = "counterPartyName", sortingDirection = "asc" } = {}
) => {
  const [options, setOptions] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const mapContentToOptions = (content = []) =>
    content.map((c) => ({
      id: normalizeId(c),
      name: c.counterPartyName ?? c.name ?? "",
      description: c.description ?? null,
    }));

  const refetch = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const resp = await fetchCounterpartiesApi(token, page, size, columnToSort, sortingDirection);
      // Handle wrapped responses with success/message
      if (resp?.success === false) {
        throw new Error(resp?.message || "Failed to load counterparties");
      }
      const content = Array.isArray(resp?.content) ? resp.content : [];
      setOptions(mapContentToOptions(content));
      setTotalElements(Number(resp?.totalElements || content.length || 0));
    } catch (e) {
      setError(e?.message || "Failed to load counterparties");
      setOptions([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [token, page, size, columnToSort, sortingDirection]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Adds a created counterparty to the local list if not present
  const addOption = useCallback((item) => {
    if (!item) return;
    const id = normalizeId(item);
    const name = item.name ?? item.counterPartyName ?? item.counterpartyName;
    const description = item.description ?? null;
    if (!id || !name) return;
    setOptions((prev) => {
      if (prev.some((p) => p.id === id)) return prev;
      return [...prev, { id, name, description }];
    });
    setTotalElements((prev) => prev + 1);
  }, []);


  // Upsert helper: update if exists, else add (no stale closure)
  const upsertOption = useCallback((item) => {
    if (!item) return;
    const id = normalizeId(item);
    const name = item.name ?? item.counterPartyName ?? item.counterpartyName;
    const description = item.description ?? null;
    if (!id) return;
    setOptions((prev) => {
      const idx = prev.findIndex((p) => p.id === id);
      if (idx === -1) {
        if (!name) return prev; // ignore if name missing for brand-new item
        return [...prev, { id, name, description }];
      }
      const updated = [...prev];
      updated[idx] = { id, name: name ?? updated[idx].name, description };
      return updated;
    });
  }, []);

  // Remove an option if present
  const removeOption = useCallback((idOrItem) => {
    const id = typeof idOrItem === "object" ? normalizeId(idOrItem) : String(idOrItem ?? "");
    if (!id) return;
    setOptions((prev) => prev.filter((p) => p.id !== id));
    setTotalElements((prev) => Math.max(0, prev - 1));
  }, []);

  // Listen to global counter-party WS updates (DTO only)
  useEffect(() => {
    const handleCounterPartyUpdate = (event) => {
      const cp = event.detail || {};
      const id = normalizeId(cp);
      if (!id) return;

      // Derive action: isActive = 0 => delete; else upsert
      const isDelete = Number(cp.isActive) === 0;

      console.log("debugging", "/topic/counter-party (trade-entry)", {
        derivedAction: isDelete ? "DELETE" : "UPSERT",
        data: cp,
      });

      if (isDelete) {
        removeOption(id);
      } else {
        upsertOption(cp);
      }
    };

    window.addEventListener("counterPartyUpdate", handleCounterPartyUpdate);
    return () => window.removeEventListener("counterPartyUpdate", handleCounterPartyUpdate);
  }, [removeOption, upsertOption]); // removed `options` from deps to avoid stale closure

  return { options, totalElements, loading, error, refetch, addOption };
};

export default useCounterparties;
